/**
 * @file Renders the switch that enables or disables two factor authentication.
 */
import React, { useRef, useState } from "react";
import { Space, Switch } from "antd";

import { useAppContext } from "@components/AppContext";
import type { Operation } from "@components/AppContext/types";
import {
  enableAccount2FAOperation as defaultEnableOperation,
  disableAccount2FAOperation as defaultDisableOperation,
  disableMyAccount2FAOperation as defaultDisableMyOperation,
  enableMyAccount2FAOperation as defaultVerificationEnableOperation,
} from "@api/services/investments";
import TwoFactorAuthenticationModal from "../TwoFactorAuthenticationModal";

const LABEL_ENABLED = "Enabled";
const LABEL_DISABLED = "Disabled";

type ModalControls = {
  open?: () => void;
};

type Props = {
  mobileNumber?: string;
  accountId?: string;
  mfaMethods?: string[];
  isDisabled?: boolean;
  isEnabled: boolean;
  enableOperation?: Operation;
  disableOperation?: Operation;
  disableMyOperation?: Operation;
  verificationEnableOperation?: Operation;
  onSuccess: (shouldEnable: boolean) => Promise<void> | void;
};

type AppContextValue = {
  request: <T = unknown>(
    operation: unknown,
    parameters?: Record<string, unknown>
  ) => Promise<T>;
  isImpersonated?: boolean;
};

/**
 * Renders the switch, and the setup modal it opens for self-service enabling.
 */
const TwoFactorAuthenticationSwitch = ({
  mobileNumber,
  accountId,
  mfaMethods = [],
  isDisabled = false,
  isEnabled,
  enableOperation = defaultEnableOperation,
  disableOperation = defaultDisableOperation,
  disableMyOperation = defaultDisableMyOperation,
  verificationEnableOperation = defaultVerificationEnableOperation,
  onSuccess,
}: Props) => {
  const modal = useRef<ModalControls>({});

  const [ isLoading, setIsLoading ] = useState(false);
  const { request, isImpersonated } = useAppContext() as AppContextValue;

  /**
   * Enables two factor for the account.
   */
  const enable = () =>
    request(enableOperation, { id: accountId });

  /**
   * Disables two factor for the account, or for the current identity.
   */
  const disable = () =>
    accountId
      ? request(disableOperation, { id: accountId })
      : request(disableMyOperation);

  /**
   * Opens the setup modal for self-service enabling, or calls the enable or
   * disable operation directly for an operator acting on another account.
   */
  const onChange = async (shouldEnable: boolean) => {
    const isSelfService = !accountId;
    const shouldOpenMethodModal = shouldEnable && isSelfService;

    if (shouldOpenMethodModal) {
      modal.current.open?.();
      return;
    }

    setIsLoading(true);

    if (shouldEnable) {
      await enable();
    } else {
      await disable();
    }

    await onSuccess(shouldEnable);
    setIsLoading(false);
  };

  /**
   * Reports the enabled state once the setup modal completes verification.
   */
  const onVerificationSuccess = async () => {
    setIsLoading(true);
    await onSuccess(true);
    setIsLoading(false);
  };

  const shouldDisableSwitch = isDisabled || Boolean(isImpersonated);

  return (
    <>
      <Space direction="vertical">
        <Switch
          checked={isEnabled}
          loading={isLoading}
          onChange={onChange}
          disabled={shouldDisableSwitch}
          checkedChildren={LABEL_ENABLED}
          unCheckedChildren={LABEL_DISABLED}
        />
      </Space>

      <TwoFactorAuthenticationModal
        modal={modal}
        mobileNumber={mobileNumber}
        mfaMethods={mfaMethods}
        isDisabled={isLoading || shouldDisableSwitch}
        verificationEnableOperation={verificationEnableOperation}
        onSuccess={onVerificationSuccess}
      />
    </>
  );
};

export default TwoFactorAuthenticationSwitch;
